import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faUpload, faEdit, faToggleOn, faToggleOff,
  faSpinner, faBook, faCheck, faSearch, faSync, faTimes,
  faFilter, faChevronDown, faTrash
} from '@fortawesome/free-solid-svg-icons';
import Layout from '../../components/Layout';
import Footer from '../../components/ui/Footer';
import CSVUploadModal from '../../components/Invoicing/CSVUploadModal';
import IFRSAccountPicker from '../../components/Invoicing/IFRSAccountPicker';
import TradeOpsDeleteModal from '../../components/TradeOpsDeleteModal';
import HMSBadge from '../../components/ui/HMSBadge';
import HMSButton from '../../components/ui/HMSButton';
import HMSEmptyState from '../../components/ui/HMSEmptyState';
import HMSPageHeader from '../../components/ui/HMSPageHeader';
import HMSSkeleton from '../../components/ui/HMSSkeleton';
import {
  fetchGLAccountsByEntity, createGLAccount, updateGLAccount, deleteGLAccount,
  uploadGLAccountsCSV, fetchVATRates, SHARED_CHART,
} from '../../services/invoicingService';
import { extractApiError } from '../../utils/errorMessages';
import { useFlashMessage } from '../../components/FlashMessage';
import { useAuth } from '../../context/AuthContext';
import './OutgoingInvoice.css';
import '../../components/Invoicing/InvoiceSearchFilter.css';

// IMT's one shared chart of accounts: what this page lists and uploads to.
export { SHARED_CHART };

const GL_ACCOUNT_TYPES = [
  { value: 'REVENUE', label: 'Revenue' },
  { value: 'EXPENSE', label: 'Expense' },
  { value: 'COST_OF_SALES', label: 'Cost of Sales' },
  { value: 'ASSET', label: 'Asset' },
  { value: 'LIABILITY', label: 'Liability' },
  { value: 'EQUITY', label: 'Equity' },
  { value: 'OTHER_INCOME', label: 'Other Income' },
  { value: 'OTHER_EXPENSE', label: 'Other Expense' },
];

const TEMPLATE_COLUMNS = [
  'GL Account Number', 'Account Description', 'Account Function',
  'Currency (ISO Code)', 'Mandatory VAT', 'VAT-Code', 'Description',
  'IFRS Account Title', 'IFRS Account Number'
];

const TEMPLATE_NOTES = [
  'Re-uploading an account number that already exists updates that account (e.g. to fill in the currency) instead of being rejected as a duplicate.',
  'Blank cells are left unchanged — an empty column never overwrites an existing value.',
  'IFRS Account Title / Number are optional and map the account to the standardized IFRS chart (e.g. 3.1.1.1 — Trade accounts payable). Codes not in the standard chart are accepted but flagged as warnings so typos surface.',
];

const GLAccountsPage = () => {
  const history = useHistory();
  const { user } = useAuth();
  const isFinancialApprover = user?.is_financial_approver || user?.is_superuser;
  const { showError, showSuccess } = useFlashMessage();
  const [accounts, setAccounts] = useState([]);
  const [vatRates, setVatRates] = useState([]);
  // IMT keeps ONLY one chart of accounts for every company (Chris 2026-10-19;
  // services/chart_of_accounts.py): this page lists, adds to and uploads it.
  const selectedEntity = SHARED_CHART;
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const [filters, setFilters] = useState({ is_active: '', currency: '', mandatory_vat: '' });
  const [showFilters, setShowFilters] = useState(false);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRef = useRef(null);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteWarning, setDeleteWarning] = useState(false);
  const [deleteWarningMsg, setDeleteWarningMsg] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadVatRates();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const toggleDropdown = (id) => {
    setActiveDropdown(activeDropdown === id ? null : id);
  };

  const loadVatRates = async () => {
    try {
      const data = await fetchVATRates();
      setVatRates(data.results || data || []);
    } catch (err) {
      console.error('Failed to load VAT rates:', err);
    }
  };

  const activeFilters = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
  const activeFiltersCount = Object.keys(activeFilters).length;

  const loadAccounts = useCallback(async (page = 1, append = false) => {
    if (append) { setLoadingMore(true); } else { setLoading(true); }
    try {
      const data = await fetchGLAccountsByEntity(selectedEntity, {
        page, search: debouncedSearch || undefined,
        filters: activeFiltersCount > 0 ? activeFilters : undefined,
      });
      const items = data.results || data || [];
      if (append) {
        setAccounts(prev => [...prev, ...items]);
      } else {
        setAccounts(items);
      }
      setTotalCount(data.count ?? items.length);
      setHasMore(!!data.next);
      setCurrentPage(page);
    } catch (err) {
      console.error('Failed to load GL accounts:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [selectedEntity, debouncedSearch, activeFiltersCount]);

  useEffect(() => { loadAccounts(1, false); }, [loadAccounts]);

  const handleLoadMore = () => { loadAccounts(currentPage + 1, true); };

  const openCreateModal = () => {
    setEditingAccount(null);
    setFormData({
      account_number: '', account_name: '', account_type: 'EXPENSE',
      currency: '', mandatory_vat: false, vat_code: '', description: '',
      ifrs_account_number: '', ifrs_account_title: '',
    });
    setShowCreateModal(true);
  };

  const openEditModal = (account) => {
    setEditingAccount(account);
    setFormData({
      account_number: account.account_number,
      account_name: account.account_name,
      account_type: account.account_type,
      currency: account.currency || '',
      mandatory_vat: account.mandatory_vat || false,
      vat_code: account.vat_code || '',
      description: account.description || '',
      ifrs_account_number: account.ifrs_account_number || '',
      ifrs_account_title: account.ifrs_account_title || '',
    });
    setShowCreateModal(true);
  };

  const handleSave = async () => {
    if (!formData.account_number || !formData.account_name) {
      showError('Account Number and Description are required.');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...formData, vat_code: formData.vat_code || null };
      // Every IMT account is shared: a new one belongs to no company, and an
      // edit never names one (the server refuses a company either way).
      if (!editingAccount) {
        payload.entity = null;
      }
      if (editingAccount) {
        await updateGLAccount(editingAccount.id, payload);
        showSuccess('GL account updated successfully.');
      } else {
        await createGLAccount(payload);
        showSuccess('GL account created successfully.');
      }
      setShowCreateModal(false);
      loadAccounts(1, false);
    } catch (err) {
      const detail = err.response?.data;
      if (detail && typeof detail === 'object') {
        showError(Object.values(detail).flat().join(', '));
      } else {
        showError('Failed to save GL account.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (account) => {
    try {
      await updateGLAccount(account.id, { is_active: !account.is_active });
      loadAccounts(1, false);
    } catch (err) {
      console.error('Toggle failed:', err);
      showError(extractApiError(err, 'The account could not be switched on or off.'));
    }
  };

  const handleUpload = async (file) => await uploadGLAccountsCSV(file, SHARED_CHART);

  const handleUploadClose = () => {
    setShowUploadModal(false);
    loadAccounts(1, false);
  };

  const openDeleteModal = (acct) => {
    setDeleteTarget(acct);
    setDeleteWarning(false);
    setDeleteWarningMsg('');
    setShowDeleteModal(true);
    setActiveDropdown(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setIsDeleting(true);
      await deleteGLAccount(deleteTarget.id);
      showSuccess('GL account permanently deleted.');
      setShowDeleteModal(false);
      setDeleteTarget(null);
      loadAccounts(1, false);
    } catch (err) {
      const data = err.response?.data;
      if (err.response?.status === 409 && data?.blockers) {
        const lines = data.blockers.map(b => b.type).join(', ');
        setDeleteWarningMsg(`${data.message}\n\nReferenced by: ${lines}`);
        setDeleteWarning(true);
      } else if (err.response?.status === 403) {
        setDeleteWarningMsg(data?.error || 'Permission denied.');
        setDeleteWarning(true);
      } else {
        showError(data?.error || 'Delete failed.');
        setShowDeleteModal(false);
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const getAccountTypeLabel = (val) => {
    const match = GL_ACCOUNT_TYPES.find(t => t.value === val);
    return match ? match.label : val;
  };

  return (
    <Layout showLeftNavSidebar={true} showRightSidebar={false}>
      <div className="invoice-page-container master-data-screen gl-accounts-screen">
        <HMSPageHeader
          onBack={() => history.push('/invoicing')}
          title="GL Accounts"
          kicker="Finance"
          subtitle="One chart of accounts for every IMT company"
          actions={
            <>
              <button className="btn-secondary" onClick={() => setShowUploadModal(true)}>
                <FontAwesomeIcon icon={faUpload} /> Upload CSV
              </button>
              <button className="btn-primary" onClick={openCreateModal}>
                <FontAwesomeIcon icon={faPlus} /> Create GL Account
              </button>
            </>
          }
        />

        <div className="search-filter-controls">
          <div className="search-filter-wrapper">
            <div className="invoice-search-filter">
              <div className="search-header">
                <div className="search-input-group">
                  <div className="search-input-container">
                    <FontAwesomeIcon icon={faSearch} className="search-icon" />
                    <input
                      type="text"
                      placeholder="Search by account number, name, description, or currency..."
                      value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                      className="search-input compact"
                    />
                    {searchTerm && (
                      <button className="clear-search" onClick={() => setSearchTerm('')}>
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    )}
                  </div>
                  <button
                    className={`filter-toggle-button ${showFilters ? 'active' : ''}`}
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    <FontAwesomeIcon icon={faFilter} />
                    Filters
                    {activeFiltersCount > 0 && <span className="filter-count">{activeFiltersCount}</span>}
                    <FontAwesomeIcon icon={faChevronDown} className={`chevron ${showFilters ? 'rotated' : ''}`} />
                  </button>
                </div>
              </div>
              {showFilters && (
                <div className="search-filters-panel">
                  <div className="filters-grid">
                    <div className="form-group">
                      <label>Status</label>
                      <select value={filters.is_active} onChange={(e) => setFilters(f => ({ ...f, is_active: e.target.value }))} className="form-select">
                        <option value="">All</option>
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Currency</label>
                      <select value={filters.currency} onChange={(e) => setFilters(f => ({ ...f, currency: e.target.value }))} className="form-select">
                        <option value="">All Currencies</option>
                        {[...new Set(accounts.map(a => a.currency).filter(Boolean))].sort().map(c => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Mandatory VAT</label>
                      <select value={filters.mandatory_vat} onChange={(e) => setFilters(f => ({ ...f, mandatory_vat: e.target.value }))} className="form-select">
                        <option value="">All</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                    </div>
                  </div>
                  <div className="filters-actions">
                    <button className="btn btn-primary btn-sm" onClick={() => { loadAccounts(1, false); setShowFilters(false); }}>Apply</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => { setFilters({ is_active: '', currency: '', mandatory_vat: '' }); setSearchTerm(''); }} disabled={activeFiltersCount === 0 && !searchTerm}>Clear All</button>
                  </div>
                </div>
              )}
            </div>
          </div>
          <button className="reload-button" onClick={() => loadAccounts(1, false)} title="Reload accounts">
            <FontAwesomeIcon icon={faSync} />
          </button>
        </div>

        <div className="invoice-list-container">
          {loading ? (
            /* Skeleton in the shape of the account table that is coming.
               `.loading-state` keeps the padding the spinner block used; only
               its centring has to go, or the bars collapse to their content
               width. */
            <div className="loading-state" style={{ alignItems: 'stretch' }}>
              <HMSSkeleton shape="title" />
              <HMSSkeleton lines={6} />
            </div>
          ) : accounts.length === 0 ? (
            <HMSEmptyState
              variant="card"
              icon={<FontAwesomeIcon icon={faBook} />}
              title={searchTerm ? 'No Results' : 'No GL Accounts'}
              hint={searchTerm ? 'No accounts match your search' : 'No GL accounts found for this entity'}
            />
          ) : (
            <>
            <table className="invoice-list-table">
              <thead>
                <tr>
                  <th>Account No.</th>
                  <th>Description</th>
                  <th>Function</th>
                  <th>Currency</th>
                  <th>Mandatory VAT</th>
                  <th>VAT Code</th>
                  <th>IFRS Account</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map(acct => (
                  <tr key={acct.id}>
                    <td className="invoice-number-cell">{acct.account_number}</td>
                    <td>{acct.account_name}</td>
                    <td>{getAccountTypeLabel(acct.account_type)}</td>
                    <td>{acct.currency || '-'}</td>
                    <td>
                      {acct.mandatory_vat
                        ? <HMSBadge status="approved"><FontAwesomeIcon icon={faCheck} /> Yes</HMSBadge>
                        : <HMSBadge status="draft">No</HMSBadge>
                      }
                    </td>
                    <td>{acct.vat_code_display || '-'}</td>
                    <td>
                      {acct.ifrs_account_number || acct.ifrs_account_title ? (
                        <div>
                          <div>{acct.ifrs_account_number || '-'}</div>
                          {acct.ifrs_account_title && (
                            <div style={{ fontSize: '0.85em', color: 'var(--text-secondary)' }}>{acct.ifrs_account_title}</div>
                          )}
                        </div>
                      ) : '-'}
                    </td>
                    <td>
                      <HMSBadge status={acct.is_active ? 'approved' : 'draft'}>
                        {acct.is_active ? 'Active' : 'Inactive'}
                      </HMSBadge>
                    </td>
                    <td className="actions-cell">
                      <div
                        className={`action-dropdown ${activeDropdown === acct.id ? 'dropdown-open' : ''}`}
                        onClick={(e) => e.stopPropagation()}
                        ref={activeDropdown === acct.id ? dropdownRef : null}
                      >
                        <button
                          className="gear-button"
                          onClick={(e) => { e.stopPropagation(); toggleDropdown(acct.id); }}
                        >
                          Actions
                        </button>
                        {activeDropdown === acct.id && (
                          <div className="dropdown-menu">
                            <button onClick={() => { setActiveDropdown(null); openEditModal(acct); }}>
                              <FontAwesomeIcon icon={faEdit} /> Edit
                            </button>
                            <hr />
                            <button onClick={() => { setActiveDropdown(null); handleToggleActive(acct); }}>
                              <FontAwesomeIcon icon={acct.is_active ? faToggleOff : faToggleOn} />
                              {acct.is_active ? ' Deactivate' : ' Activate'}
                            </button>
                            {isFinancialApprover && (
                              <>
                                <hr />
                                <button className="delete-action" onClick={() => openDeleteModal(acct)}>
                                  <FontAwesomeIcon icon={faTrash} /> Delete
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="master-data-load-more">
              <span className="load-more-count">
                Showing {accounts.length.toLocaleString()} of {totalCount.toLocaleString()}
              </span>
              {hasMore && (
                <HMSButton variant="ghost" size="compact" onClick={handleLoadMore} disabled={loadingMore}>
                  {loadingMore ? <><FontAwesomeIcon icon={faSpinner} spin /> Loading...</> : 'Show 100 more'}
                </HMSButton>
              )}
            </div>
            </>
          )}
        </div>

        <Footer />
      </div>

      {showCreateModal && (
        <div className="md-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="md-modal" onClick={(e) => e.stopPropagation()}>
            <div className="md-modal-header">
              <div className="md-modal-header-text">
                <FontAwesomeIcon icon={editingAccount ? faEdit : faPlus} className="md-modal-header-icon" />
                <h2>{editingAccount ? 'Edit GL Account' : 'Create GL Account'}</h2>
              </div>
              <button className="md-modal-close" onClick={() => setShowCreateModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="md-modal-body">
              <div className="md-form-group">
                <label className="md-form-label">Account Number <span className="required">*</span></label>
                <input className="md-form-input" type="text" value={formData.account_number} onChange={(e) => setFormData({ ...formData, account_number: e.target.value })} placeholder="e.g. 4000" />
              </div>
              <div className="md-form-group">
                <label className="md-form-label">Account Function <span className="required">*</span></label>
                <select className="md-form-select" value={formData.account_type} onChange={(e) => setFormData({ ...formData, account_type: e.target.value })}>
                  {GL_ACCOUNT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div className="md-form-group">
                <label className="md-form-label">Account Description <span className="required">*</span></label>
                <input className="md-form-input" type="text" value={formData.account_name} onChange={(e) => setFormData({ ...formData, account_name: e.target.value })} placeholder="e.g. Office Rent" />
              </div>
              <div className="md-form-group">
                <label className="md-form-label">Currency (ISO Code)</label>
                <input className="md-form-input" type="text" value={formData.currency} onChange={(e) => setFormData({ ...formData, currency: e.target.value.toUpperCase() })} placeholder="e.g. ZAR, USD, EUR" maxLength={3} />
              </div>
              <div className="md-form-group">
                <label className="md-form-label">VAT Code</label>
                <select className="md-form-select" value={formData.vat_code} onChange={(e) => setFormData({ ...formData, vat_code: e.target.value })}>
                  <option value="">-- None --</option>
                  {vatRates.map(v => <option key={v.id} value={v.id}>{v.vat_code} ({v.rate_percentage}%)</option>)}
                </select>
              </div>
              <div className="md-form-group">
                <label className="md-form-checkbox">
                  <input type="checkbox" checked={formData.mandatory_vat} onChange={(e) => setFormData({ ...formData, mandatory_vat: e.target.checked })} />
                  Mandatory VAT on posting
                </label>
              </div>
              <div className="md-form-group">
                <label className="md-form-label">Description</label>
                <input className="md-form-input" type="text" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Detailed description of account usage" />
              </div>
              <IFRSAccountPicker
                number={formData.ifrs_account_number || ''}
                title={formData.ifrs_account_title || ''}
                onChange={({ ifrs_account_number, ifrs_account_title }) =>
                  setFormData({ ...formData, ifrs_account_number, ifrs_account_title })
                }
              />
            </div>
            <div className="md-modal-footer">
              <button className="md-btn-cancel" onClick={() => setShowCreateModal(false)}>Cancel</button>
              <button className="md-btn-submit" onClick={handleSave} disabled={saving}>
                {saving ? <><FontAwesomeIcon icon={faSpinner} spin /> Saving...</> : editingAccount ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      <CSVUploadModal
        isOpen={showUploadModal} onClose={handleUploadClose} onUpload={handleUpload}
        title="Upload GL Accounts" templateColumns={TEMPLATE_COLUMNS} templateNotes={TEMPLATE_NOTES}
        entityRequired={false}
      />

      <TradeOpsDeleteModal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setDeleteTarget(null); }}
        onConfirm={handleDelete}
        itemType="master_data"
        itemDetails={deleteTarget ? {
          recordType: 'GL Account',
          recordCode: deleteTarget.account_number,
          recordName: deleteTarget.account_name,
        } : {}}
        isWarning={deleteWarning}
        warningMessage={deleteWarningMsg}
        isDeleting={isDeleting}
      />
    </Layout>
  );
};

export default GLAccountsPage;
